import React, { memo } from "react";

/**
 * Unwraps single, double, or triple JSON-stringified payloads
 */
function unwrapPayload(raw) {
  if (!raw) return null;
  let current = raw;
  let attempts = 0;
  while (typeof current === "string" && attempts < 5) {
    const trimmed = current.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        current = JSON.parse(trimmed);
        attempts++;
      } catch (_) {
        break;
      }
    } else {
      break;
    }
  }
  return current;
}

/**
 * Extract structured interactive content from Meta payloads
 */
function extractInteractiveData(paymentData) {
  const payload =
    unwrapPayload(paymentData?.message_paylode) ||
    unwrapPayload(paymentData?.template_paylode) ||
    unwrapPayload(paymentData?.payload) ||
    unwrapPayload(paymentData?.body);

  const msg =
    payload?.entry?.[0]?.changes?.[0]?.value?.messages?.[0] ||
    payload?.messages?.[0] ||
    payload;

  const interactive = msg?.interactive || payload?.interactive;

  // 1. Call Permission Reply (Meta WhatsApp Calling Permission)
  if (interactive?.call_permission_reply || interactive?.type === "call_permission_reply") {
    const cpr = interactive?.call_permission_reply || interactive;
    const response = (cpr.response || "accept").toLowerCase();
    const isPermanent = Boolean(cpr.is_permanent);
    const source = cpr.response_source || "user_action";

    return {
      type: "call_permission_reply",
      response, // 'accept' or 'reject'
      isPermanent,
      source,
      title: response === "accept" ? "Call Permission Allowed" : "Call Permission Declined",
    };
  }

  // 2. Button Reply
  if (interactive?.button_reply) {
    return {
      type: "button_reply",
      title: interactive.button_reply.title || interactive.button_reply.id || "Button Selection",
      id: interactive.button_reply.id,
    };
  }

  // 3. List Reply
  if (interactive?.list_reply) {
    return {
      type: "list_reply",
      title: interactive.list_reply.title || interactive.list_reply.id || "List Selection",
      description: interactive.list_reply.description || null,
      id: interactive.list_reply.id,
    };
  }

  // 4. Flows / NFM Reply
  if (interactive?.nfm_reply) {
    let responseData = interactive.nfm_reply.response_json;
    if (typeof responseData === "string") {
      try { responseData = JSON.parse(responseData); } catch (_) {}
    }
    return {
      type: "nfm_reply",
      name: interactive.nfm_reply.name || "Flow Form Response",
      body: interactive.nfm_reply.body || null,
      responseData,
    };
  }

  // 5. Standard Button
  if (msg?.button?.text) {
    return {
      type: "button_reply",
      title: msg.button.text,
      payload: msg.button.payload,
    };
  }

  // 6. Outgoing Interactive Card (CTA URL / Action Button)
  if (interactive?.action || interactive?.body?.text) {
    const act = interactive.action;
    return {
      type: "outgoing_card",
      headerText: interactive.header?.text || null,
      bodyText: interactive.body?.text || null,
      footerText: interactive.footer?.text || null,
      buttonText: act?.parameters?.display_text || act?.buttons?.[0]?.reply?.title || "Open",
      url: act?.parameters?.url || null,
    };
  }

  // 7. Direct Plain Text
  if (paymentData?.body && typeof paymentData.body === "string" && !paymentData.body.trim().startsWith("{")) {
    return {
      type: "text",
      title: paymentData.body,
    };
  }

  return null;
}

const Interactive = memo(({ paymentData }) => {
  const data = extractInteractiveData(paymentData);

  // 1. Dedicated Call Permission Reply UI (Accept / Reject)
  if (data?.type === "call_permission_reply") {
    const isAccepted = data.response === "accept";

    return (
      <div
        className="rounded-3 p-2 my-1 shadow-sm"
        style={{
          minWidth: "220px",
          maxWidth: "320px",
          backgroundColor: isAccepted ? "#F0FDF4" : "#FEF2F2",
          border: `1px solid ${isAccepted ? "#86EFAC" : "#FECACA"}`,
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-1">
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: "1.2rem" }}>{isAccepted ? "📞" : "📵"}</span>
            <span
              className="fw-bold"
              style={{
                fontSize: "0.88rem",
                color: isAccepted ? "#166534" : "#991B1B",
              }}
            >
              {isAccepted ? "Call Permission Allowed" : "Call Permission Declined"}
            </span>
          </div>
          <span
            className="badge rounded-pill px-2 py-1"
            style={{
              fontSize: "0.7rem",
              fontWeight: "600",
              backgroundColor: isAccepted ? "#22C55E" : "#EF4444",
              color: "#FFFFFF",
            }}
          >
            {isAccepted ? "✓ Allowed" : "✗ Declined"}
          </span>
        </div>

        <div
          className="ps-4 text-muted"
          style={{
            fontSize: "0.78rem",
            color: isAccepted ? "#15803D" : "#B91C1C",
            lineHeight: "1.3",
          }}
        >
          {isAccepted
            ? `Customer granted permission for voice & video calls${data.isPermanent ? " (Always allow)" : ""}.`
            : "Customer declined call permission for this session."}
        </div>
      </div>
    );
  }

  // 2. Button Reply from customer
  if (data?.type === "button_reply") {
    return (
      <div
        className="d-inline-flex align-items-center gap-2 px-3 py-2 my-1 rounded-3 shadow-sm"
        style={{
          backgroundColor: "#F0F2F5",
          border: "1px solid #E2E8F0",
          maxWidth: "100%",
        }}
      >
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            backgroundColor: "#00A884",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "11px",
            fontWeight: "bold",
            flexShrink: 0,
          }}
        >
          ✓
        </div>
        <span
          className="fw-semibold text-truncate"
          style={{
            color: "#111B21",
            fontSize: "0.93rem",
          }}
        >
          {data.title}
        </span>
      </div>
    );
  }

  // 3. List Reply from customer
  if (data?.type === "list_reply") {
    return (
      <div
        className="p-2 my-1 rounded-3 shadow-sm"
        style={{
          backgroundColor: "#F0F2F5",
          border: "1px solid #E2E8F0",
          minWidth: "180px",
          maxWidth: "100%",
        }}
      >
        <div className="d-flex align-items-center gap-2 mb-1">
          <span style={{ fontSize: "16px" }}>📋</span>
          <span className="fw-semibold" style={{ color: "#111B21", fontSize: "0.93rem" }}>
            {data.title}
          </span>
        </div>
        {data.description && (
          <div className="text-muted ps-4" style={{ fontSize: "0.82rem", lineHeight: "1.3" }}>
            {data.description}
          </div>
        )}
      </div>
    );
  }

  // 4. WhatsApp Flows (NFM Form)
  if (data?.type === "nfm_reply") {
    return (
      <div className="p-2 my-1 rounded-3 border shadow-sm bg-white" style={{ minWidth: "220px", maxWidth: "100%" }}>
        <div className="d-flex align-items-center gap-2 pb-2 mb-2 border-bottom">
          <span style={{ fontSize: "18px" }}>📝</span>
          <span className="fw-bold" style={{ fontSize: "0.9rem", color: "#111B21" }}>
            {data.name}
          </span>
        </div>
        {data.responseData && typeof data.responseData === "object" ? (
          <div className="d-flex flex-column gap-1">
            {Object.entries(data.responseData).map(([k, v]) => (
              <div key={k} className="d-flex justify-content-between gap-2 p-1 px-2 rounded bg-light" style={{ fontSize: "0.8rem" }}>
                <span className="text-muted text-capitalize">{k.replace(/_/g, " ")}:</span>
                <span className="fw-semibold text-dark text-truncate" style={{ maxWidth: "150px" }}>
                  {typeof v === "object" ? JSON.stringify(v) : String(v)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          data.responseData && <div className="p-2 bg-light rounded text-muted" style={{ fontSize: "0.8rem" }}>{String(data.responseData)}</div>
        )}
      </div>
    );
  }

  // 5. Outgoing CTA Card
  if (data?.type === "outgoing_card") {
    return (
      <div className="bg-white rounded overflow-hidden border shadow-sm" style={{ minWidth: "220px", maxWidth: "320px" }}>
        {data.headerText && <div className="p-2 px-3 fw-bold border-bottom" style={{ fontSize: "0.95rem", color: "#111B21" }}>{data.headerText}</div>}
        {data.bodyText && <div className="p-3" style={{ fontSize: "0.92rem", color: "#111B21", whiteSpace: "pre-wrap" }}>{data.bodyText}</div>}
        {data.url && (
          <div className="border-top">
            <a href={data.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm w-100 py-2 text-decoration-none" style={{ color: "#00a884", fontWeight: "600" }}>
              🔗 {data.buttonText || "Open Link"}
            </a>
          </div>
        )}
      </div>
    );
  }

  // 6. Direct Text
  if (data?.type === "text" && data.title) {
    return (
      <p className="m-0 mb-1" style={{ fontSize: "0.95rem", color: "#111B21", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {data.title}
      </p>
    );
  }

  // 7. General Fallback
  return (
    <div
      className="d-inline-flex align-items-center gap-2 px-3 py-2 my-1 rounded-3 shadow-sm"
      style={{
        backgroundColor: "#F0F2F5",
        border: "1px solid #E2E8F0",
        maxWidth: "100%",
      }}
    >
      <span style={{ fontSize: "14px" }}>🔘</span>
      <span className="fw-semibold text-dark" style={{ fontSize: "0.93rem" }}>
        {paymentData?.body || "Interactive Response"}
      </span>
    </div>
  );
});

export default memo(Interactive);